import _ from '@lodash';
import { useEffect, useState, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import { determineEditableAccess } from '../EditFormUtils';

function EditFormAutoCompleteField(props) {
	const { control, getValues } = useFormContext();
	const { error } = props.fieldState;
	const { value, onChange } = props.field;
	const [open, setOpen] = useState(false);
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const isMultiple = !!(props.fieldConfig && props.fieldConfig.multiple);
	const [autocompleteValue, setAutocompleteValue] = useState(isMultiple ? [] : null);
	const [readOnly, setReadOnly] = useState(determineEditableAccess(props.config, props.fieldConfig, props.fieldPrefix));
	const queryTimeout = useRef(null);
	const valueKey = props.fieldConfig && props.fieldConfig.type && props.fieldConfig.type === 'enum' ? 'value' : 'id';
	const labelKey = valueKey === 'value' ? 'label' : 'name';
	const queryCharacter = valueKey === 'id' ? '#' : '';

	const parseFilterKey = () => {
		if (props.fieldConfig.filterKey) {
			if (_.isArray(props.fieldConfig.filterKey)) {
				return props.fieldConfig.filterKey.map(f => (!f.startsWith('^') ? (props.fieldPrefix || '') + f : f.substring(1)));
			}
			return !props.fieldConfig.filterKey.startsWith('^') ? (props.fieldPrefix || '') + props.fieldConfig.filterKey : props.fieldConfig.filterKey.substring(1);
		}
		return null;
	};

	const filterValue = useWatch({
		control,
		name: parseFilterKey()
		//defaultValue: null TODO: gondot okoz ha benne van
	});

	const normalizeValueArray = () => {
		if (value === null || value === undefined) return [];
		if (isMultiple) {
			if (_.isArray(value)) return value.filter(v => v !== null && v !== undefined);
			return [value];
		}
		return [value];
	};

	const getQuery = () => {
		if (inputValue && inputValue === queryCharacter) {
			setOptions([]);
			setOpen(false);
		} else {
			setLoading(true);
			props.fieldConfig
				.query(inputValue || '', props.fieldConfig.limit || 100, props.fieldConfig.itemsParameterIsDto ? getValues() : filterValue)
				.then(resp => {
					setLoading(false);
					if (resp && resp.length > 0) {
						setOptions(resp);
					} else {
						setOptions([]);
						setOpen(false);
					}
				});
		}
	};

	const updateByCurrentValue = () => {
		const valuesArr = normalizeValueArray();
		const itemsParam = props.fieldConfig.itemsParameterIsDto ? getValues() : filterValue;

		if (!isMultiple) {
			const v = valuesArr.length > 0 ? valuesArr[0] : null;
			if (!v) {
				setAutocompleteValue(null);
				return;
			}
			props.fieldConfig.query(`${queryCharacter}${v}`, 1, itemsParam).then(resp => {
				if (resp && resp.length > 0 && resp[0][valueKey] === v) {
					setAutocompleteValue(resp[0]);
				} else {
					setAutocompleteValue(null);
					onChange(null);
				}
			});
		} else {
			if (valuesArr.length === 0) {
				setAutocompleteValue([]);
				return;
			}
			Promise.all(
				valuesArr.map(v =>
					props.fieldConfig
						.query(`${queryCharacter}${v}`, 1, itemsParam)
						.then(resp => (resp && resp.length > 0 ? resp[0] : null))
				)
			).then(results => {
				const validOptions = results.filter(o => o && valuesArr.includes(o[valueKey]));
				if (validOptions.length > 0) {
					setAutocompleteValue(validOptions);
				} else {
					setAutocompleteValue([]);
					onChange([]);
				}
			});
		}
	};

	useEffect(() => {
		if (open) {
			if (queryTimeout && queryTimeout.current) clearTimeout(queryTimeout.current);
			queryTimeout.current = setTimeout(() => {
				if (open) {
					getQuery();
				}
			}, 500);
		}
	}, [inputValue]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleAutoCompleteOnChange = (e, v, r) => {
		if (!isMultiple) {
			if (r === 'select-option') {
				onChange(v[valueKey]);
				setAutocompleteValue(v);
				setOptions([v]);
			} else if (r === 'clear') {
				onChange(null);
				setAutocompleteValue(null);
			}
		} else {
			if (r === 'select-option' || r === 'remove-option') {
				const newIds = v && v.length ? v.map(o => o[valueKey]) : [];
				onChange(newIds);
				setAutocompleteValue(v || []);
				setOptions(v || []);
			} else if (r === 'clear') {
				onChange([]);
				setAutocompleteValue([]);
			}
		}
	};

	useEffect(() => {
		const valuesArr = normalizeValueArray();
		if (!open) {
			if (!isMultiple) {
				if (valuesArr.length > 0 && autocompleteValue === null) {
					updateByCurrentValue();
				}
			} else {
				if (valuesArr.length > 0 && (!autocompleteValue || autocompleteValue.length === 0)) {
					updateByCurrentValue();
				}
			}
		}
	}, [value]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const valuesArr = normalizeValueArray();
		if (valuesArr.length > 0) {
			updateByCurrentValue();
		} else {
			if (isMultiple) {
				setAutocompleteValue([]);
			} else {
				setAutocompleteValue(null);
			}
		}
	}, [props.fieldConfig, filterValue]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Autocomplete
			multiple={isMultiple}
			disableCloseOnSelect={isMultiple}
			filterSelectedOptions={isMultiple}
			open={open}
			onOpen={() => {
				setOpen(true);
				if (!inputValue || inputValue.length === 0) getQuery();
			}}
			onClose={() => {
				setOpen(false);
			}}
			inputValue={inputValue}
			onInputChange={(e, v) => setInputValue(v)}
			getOptionSelected={(option, val) => option[valueKey] === val[valueKey]}
			getOptionLabel={option => (option && option[labelKey] ? option[labelKey] : '')}
			defaultValue={isMultiple ? [] : null}
			value={autocompleteValue}
			onChange={(e, v, r) => handleAutoCompleteOnChange(e, v, r)}
			options={options}
			loading={loading}
			filterOptions={opt => opt}
			noOptionsText="Nincs találat"
			renderInput={params => (
				<TextField
					{...params}
					variant="outlined"
					className="mt-8 mb-16"
					error={error}
					required={props.fieldConfig.required}
					helperText={error ? error.message : props.fieldConfig.helperText}
					label={props.fieldConfig.text}
					id={props.field.name}
					fullWidth
					InputProps={{
						...params.InputProps,
						readOnly,
						endAdornment: (
							<>
								{loading ? <CircularProgress color="inherit" size={20} /> : null}
								{params.InputProps.endAdornment}
							</>
						)
					}}
				/>
			)}
		/>
	);
}

export default EditFormAutoCompleteField;